import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { TokenSession } from '@avangard/utils/token';
import { useNavigate } from '@avangard/utils/routing';
import { black } from '@avangard/ui/colors';
import { Button, Modal, CodeField } from '@avangard/ui/core';

import { routes } from '@config/routes';
import { FormStyled } from '@modules/layout/styled';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { LoginTFAMutation } from '@modules/auth/graphql';

import type { LoginTfaMutationType, LoginTfaMutationVariables } from '@modules/types/graphql';

type SigninTfaFormProps = {
    className?: string;
    tfaToken: string;
    // Вызывается, когда сессия подтверждения входа больше не годится (истекла, уже
    // использована, недействительна): продолжить можно только вводом пароля заново.
    onSessionExpired: (message: { title: string; description: string }) => void;
};

// Коды ошибок бэкенда (extensions.code), после которых код TOTP вводить бессмысленно.
const SESSION_ENDED_CODES = [
    'pre_auth_token_expired',
    'pre_auth_token_invalid',
    'pre_auth_token_used',
];

type GraphqlErrorLike = {
    message?: string;
    extensions?: { code?: string; description?: string };
};

const findSessionEndedError = (error: any): GraphqlErrorLike | null => {
    const errors: GraphqlErrorLike[] =
        error?.networkError?.result?.errors ?? error?.graphQLErrors ?? [];

    return errors.find(item => SESSION_ENDED_CODES.includes(item?.extensions?.code ?? '')) ?? null;
};

const Root = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 300px;
    width: 100%;
`;

const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;

    > * {
        width: 100%;
    }

    > h3 {
        color: ${black[80]};
        font-size: 2.4rem;
        font-weight: 500;
    }

    > p {
        font-size: 1.4rem;
        line-height: 1.5;
        margin-top: 20px;
    }
`;

const Main = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin-top: 24px;
`;

const StyledForm = styled(Form)`
    width: 100%;
`;

const StyledFormRowWithMargin = styled(FormStyled.FormRowWithMargin)`
    &:not(:last-of-type) {
        margin-bottom: 12px;
    }
`;

const StyledCodeField = styled(CodeField)`
    margin-bottom: 24px;
`;

const Content = styled.div`
    font-size: 1.4rem;
`;

const Footer = styled.div`
    margin-top: 10px;
    width: 100%;
    display: flex;
    justify-content: flex-end;
`;

const SigninTfaForm = (props: SigninTfaFormProps): React.ReactElement => {
    const { tfaToken, onSessionExpired, ...otherProps } = props;
    const { t } = useTranslation(['common', 'errors', 'auth']);

    const navigate = useNavigate();
    const [openCantSignInModal, setCantSignInModal] = React.useState(false);

    const [loginTfa] = useMutation<LoginTfaMutationType, LoginTfaMutationVariables>(
        LoginTFAMutation,
    );

    const handleOpenCantSignInModal = (): void => setCantSignInModal(true);
    const handleCloseCantSignInModal = (): void => setCantSignInModal(false);

    return (
        <>
            <Root {...otherProps}>
                <Header>
                    <h3>{t('auth:blocks.totp.confirm_totp')}</h3>
                    <p>{t('auth:blocks.totp.enter_totp_сode')}</p>
                </Header>

                <Main>
                    <ExtendedFormik
                        validateOnChange={false}
                        validateOnBlur={false}
                        validationSchema={yup.object({
                            totpCode: yup
                                .string()
                                .required(t('errors:validations.required') ?? '')
                                .min(6, t('errors:validations.required') ?? ''),
                        })}
                        initialValues={{
                            totpCode: '',
                        }}
                        onSubmit={async values => {
                            try {
                                const { data: loginTfaData } = await loginTfa({
                                    variables: {
                                        token: tfaToken,
                                        totpCode: values.totpCode,
                                    },
                                });

                                if (
                                    loginTfaData?.loginTFA?.accessToken &&
                                    loginTfaData?.loginTFA?.refreshToken
                                ) {
                                    TokenSession.setCurrentSession(loginTfaData?.loginTFA);

                                    await navigate(routes.index.path);
                                }
                            } catch (e) {
                                const sessionEnded = findSessionEndedError(e);

                                if (sessionEnded) {
                                    onSessionExpired({
                                        title: sessionEnded.message ?? '',
                                        description: sessionEnded.extensions?.description ?? '',
                                    });

                                    return;
                                }

                                // неверный код, блокировка и прочие ошибки показывает ExtendedFormik
                                throw e;
                            }
                        }}
                    >
                        {formikProps => {
                            const { values, errors, setFieldValue, handleBlur, isSubmitting } =
                                formikProps;

                            const handleChangeCode = (code: string) => {
                                setFieldValue('totpCode', code);
                            };

                            return (
                                <StyledForm>
                                    <StyledCodeField
                                        fullWidth
                                        id='totpCode'
                                        name='totpCode'
                                        onChange={handleChangeCode}
                                        onBlur={handleBlur}
                                        value={values.totpCode}
                                        error={!!errors.totpCode}
                                        helperText={errors.totpCode}
                                    />

                                    <StyledFormRowWithMargin>
                                        <Button fullWidth type='submit' loading={isSubmitting}>
                                            {t('common:actions.enter')}
                                        </Button>
                                    </StyledFormRowWithMargin>

                                    <StyledFormRowWithMargin>
                                        <Button
                                            fullWidth
                                            variant='text'
                                            type='button'
                                            disabled={isSubmitting}
                                            onClick={handleOpenCantSignInModal}
                                        >
                                            {t('auth:blocks.totp.confirm_totp_problems')}
                                        </Button>
                                    </StyledFormRowWithMargin>
                                </StyledForm>
                            );
                        }}
                    </ExtendedFormik>
                </Main>
            </Root>

            <Modal
                open={openCantSignInModal}
                title={t('auth:blocks.totp.login_problems') ?? ''}
                onClose={handleCloseCantSignInModal}
            >
                <Content>{t('auth:blocks.totp.responsible_person')}</Content>
                <Footer>
                    <Button onClick={handleCloseCantSignInModal}>
                        {t('common:actions.close')}
                    </Button>
                </Footer>
            </Modal>
        </>
    );
};

export { SigninTfaForm };
